import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Lock, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ReasonDialog } from '@/components/accountant/reason-dialog';
import { Combobox, type ComboItem } from '@/components/combobox';
import { DateInput } from '@/components/date-input';
import { Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DrillLink } from '@/features/reports/report-table';
import { fyStart } from '@/features/reports/settings';
import {
  movableAccount,
  selectionTotal,
  useReclassifyLines,
  type ReclassifyFilters,
  type ReclassifyLine,
  type ReclassifyResult,
} from '@/lib/accountant';
import { useAccounts, useTaxCodes } from '@/lib/accounts';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { useContacts } from '@/lib/contacts';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { sourceLabel } from '@/lib/journal';
import { formatMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useCompanySettings } from '@/lib/settings';
import { homeCurrency } from '@/lib/org-locale';

export const Route = createFileRoute('/accounting/reclassify')({ component: Reclassify });

const TYPES: Array<[string, string]> = [
  ['', 'All transaction types'],
  ['manual', 'Journal entry'],
  ['invoice', 'Invoice'],
  ['credit_note', 'Credit note'],
  ['sales_receipt', 'Sales receipt'],
  ['bill', 'Bill'],
  ['debit_note', 'Debit note'],
  ['expense', 'Expense'],
  ['bank', 'Bank transaction'],
  ['opening', 'Opening balance'],
];

/** Screen A8 (document 06): Reclassify transactions, QBO Accountant tools. */
function Reclassify() {
  const orgId = useOrgId();
  const me = useMe();
  const qc = useQueryClient();
  const company = useCompanySettings();
  const accounts = useAccounts();
  const taxCodes = useTaxCodes();
  const contacts = useContacts();
  const today = todayIso();
  const fy = fyStart(today, company.data?.advanced.fiscalYearStart ?? 1);
  const [draft, setDraft] = useState<ReclassifyFilters>({
    from: fy,
    to: today,
    accountTypes: 'all',
  });
  const [filters, setFilters] = useState<ReclassifyFilters>(draft);
  const lines = useReclassifyLines(filters);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [toAccount, setToAccount] = useState<string | null>(null);
  const [toTax, setToTax] = useState('');
  const [confirming, setConfirming] = useState(false);
  const isStaff = me.data?.isStaff ?? false;

  const accountItems: ComboItem[] = useMemo(
    () =>
      (accounts.data ?? []).filter(movableAccount).map((a) => ({
        id: a.id,
        label: `${a.code} ${a.fullName}`,
        keywords: a.accountTypeName,
        group: a.accountTypeName,
      })),
    [accounts.data],
  );
  const contactItems: ComboItem[] = useMemo(
    () => (contacts.data ?? []).map((c) => ({ id: c.id, label: c.displayName })),
    [contacts.data],
  );
  const zeroCodes = (taxCodes.data ?? []).filter(
    (t) => t.isActive && Number(t.rate) === 0 && t.kind !== 'reverse_charge',
  );

  const rows = lines.data ?? [];
  const selectable = rows.filter((l) => !l.blocked);
  const chosen = rows.filter((l) => selected.has(l.lineId));
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allOn = selectable.length > 0 && selectable.every((l) => selected.has(l.lineId));

  const apply = useMutation({
    mutationFn: (reason: string) =>
      api<ReclassifyResult>(`/orgs/${orgId}/reclassify`, {
        method: 'POST',
        body: JSON.stringify({
          lineIds: [...selected],
          ...(toAccount ? { toAccountId: toAccount } : {}),
          ...(toTax ? { toTaxCodeId: toTax } : {}),
          reason,
        }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      setSelected(new Set());
      setConfirming(false);
      const first = r.entries[0];
      toast(
        `${r.lines} line${r.lines === 1 ? '' : 's'} reclassified in ${r.entries.length} transaction${r.entries.length === 1 ? '' : 's'}.`,
        first
          ? { link: { to: `/accounting/journal-entries/${first.entryId}`, label: 'View' } }
          : {},
      );
    },
  });

  if (!orgId) return <EmptyState title="Select a client" />;
  const target = accountItems.find((a) => a.id === toAccount)?.label;
  const taxLabel =
    toTax === 'none' ? 'no VAT code' : (zeroCodes.find((t) => t.id === toTax)?.code ?? '');

  return (
    <div>
      <PageHeader
        title="Reclassify transactions"
        description="Move posted lines to another account or a VAT code without VAT. Each transaction is reversed and posted again on its own date; closed periods, filed VAT returns, reconciled lines and submitted e-invoices stay as they are."
      />
      <form
        className="mb-3 grid grid-cols-2 gap-3 rounded-lg border bg-card p-4 md:grid-cols-4 xl:grid-cols-7"
        onSubmit={(e) => {
          e.preventDefault();
          setSelected(new Set());
          setFilters(draft);
        }}
      >
        <Field label="Account" className="xl:col-span-2">
          {() => (
            <Combobox
              ariaLabel="Account"
              items={accountItems}
              value={draft.accounts?.[0] ?? null}
              placeholder="All accounts"
              onChange={(id) => setDraft({ ...draft, accounts: id ? [id] : [] })}
            />
          )}
        </Field>
        <Field label="Account types">
          {(id) => (
            <NativeSelect
              id={id}
              value={draft.accountTypes ?? 'all'}
              onChange={(e) =>
                setDraft({ ...draft, accountTypes: e.target.value as 'all' | 'pl' | 'bs' })
              }
            >
              <option value="all">All</option>
              <option value="pl">Profit and loss</option>
              <option value="bs">Balance sheet</option>
            </NativeSelect>
          )}
        </Field>
        <Field label="From">
          {(id) => (
            <DateInput
              id={id}
              value={draft.from ?? ''}
              onChange={(v) => setDraft({ ...draft, from: v })}
            />
          )}
        </Field>
        <Field label="To">
          {(id) => (
            <DateInput
              id={id}
              value={draft.to ?? ''}
              onChange={(v) => setDraft({ ...draft, to: v })}
            />
          )}
        </Field>
        <Field label="Name">
          {() => (
            <Combobox
              ariaLabel="Name"
              items={contactItems}
              value={draft.contactId ?? null}
              placeholder="All names"
              onChange={(id) =>
                setDraft({ ...draft, ...(id ? { contactId: id } : { contactId: '' }) })
              }
            />
          )}
        </Field>
        <Field label="Transaction type">
          {(id) => (
            <NativeSelect
              id={id}
              value={draft.types?.[0] ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, types: e.target.value ? [e.target.value] : [] })
              }
            >
              {TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <div className="col-span-2 flex items-end gap-2 md:col-span-4 xl:col-span-7">
          <div className="relative w-80">
            <Search
              className="absolute top-2.5 left-2.5 size-4 text-muted-foreground"
              aria-hidden
            />
            <Input
              className="pl-8"
              placeholder="Memo, number or name"
              aria-label="Search"
              value={draft.search ?? ''}
              onChange={(e) => setDraft({ ...draft, search: e.target.value })}
            />
          </div>
          <Button type="submit">Find transactions</Button>
        </div>
      </form>

      {isStaff && selected.size ? (
        <div
          className="sticky top-0 z-10 mb-3 flex flex-wrap items-end gap-3 rounded-lg border border-primary/30 bg-accent px-4 py-3"
          data-testid="reclassify-bar"
        >
          <p className="mr-2 text-sm">
            <span className="font-medium">{selected.size} selected</span>
            <span className="tnum ml-2 text-muted-foreground">
              net {homeCurrency()} {formatMinor(selectionTotal(rows, selected))}
            </span>
          </p>
          <Field label="Change account to" className="w-72">
            {() => (
              <Combobox
                ariaLabel="Change account to"
                items={accountItems}
                value={toAccount}
                placeholder="Keep the account"
                onChange={setToAccount}
              />
            )}
          </Field>
          <Field label="Change VAT code to" className="w-56">
            {(id) => (
              <NativeSelect id={id} value={toTax} onChange={(e) => setToTax(e.target.value)}>
                <option value="">Keep the VAT code</option>
                <option value="none">No VAT code</option>
                {zeroCodes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.code} – {t.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Button disabled={!toAccount && !toTax} onClick={() => setConfirming(true)}>
            Reclassify
          </Button>
          <Button variant="ghost" onClick={() => setSelected(new Set())}>
            Clear selection
          </Button>
        </div>
      ) : null}

      {lines.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : lines.isError ? (
        <EmptyState title="Could not load transactions" hint={lines.error.message} />
      ) : !rows.length ? (
        <EmptyState
          title="No lines match"
          hint="Change the account, dates or type. Bank, card, A/R, A/P and VAT lines are never listed here."
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  {isStaff ? (
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      className="size-4 accent-primary"
                      checked={allOn}
                      onChange={() =>
                        setSelected(allOn ? new Set() : new Set(selectable.map((l) => l.lineId)))
                      }
                    />
                  ) : null}
                </TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>No.</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Memo/description</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>VAT</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((l) => (
                <LineRow
                  key={l.lineId}
                  line={l}
                  staff={isStaff}
                  checked={selected.has(l.lineId)}
                  onToggle={() => toggle(l.lineId)}
                />
              ))}
            </TableBody>
          </Table>
          {rows.length >= 300 ? (
            <p className="border-t px-4 py-2 text-xs text-muted-foreground">
              Showing the latest 300 lines; narrow the filters to see more.
            </p>
          ) : null}
        </div>
      )}

      <ReasonDialog
        open={confirming}
        title={`Reclassify ${chosen.length} line${chosen.length === 1 ? '' : 's'}?`}
        description={
          <>
            {target ? <>The lines move to {target}. </> : null}
            {toTax ? <>Their VAT code becomes {taxLabel}. </> : null}
            Each transaction affected is reversed and posted again on its own date with the change;
            on an invoice or bill, every line on the same account moves with it.
          </>
        }
        confirmLabel="Reclassify"
        pending={apply.isPending}
        error={apply.error?.message ?? null}
        onConfirm={(reason) => apply.mutate(reason)}
        onClose={() => {
          apply.reset();
          setConfirming(false);
        }}
      />
    </div>
  );
}

function LineRow({
  line: l,
  staff,
  checked,
  onToggle,
}: {
  line: ReclassifyLine;
  staff: boolean;
  checked: boolean;
  onToggle: () => void;
}) {
  const drill = {
    kind: 'entry' as const,
    id: l.entryId,
    ...(l.sourceId && l.sourceType !== 'manual'
      ? { source: { type: l.sourceType, id: l.sourceId } }
      : {}),
  };
  return (
    <TableRow data-testid={`line-${l.lineId}`} className={l.blocked ? 'text-muted-foreground' : ''}>
      <TableCell>
        {l.blocked ? (
          <span title={l.blocked} aria-label={`Locked: ${l.blocked}`}>
            <Lock className="size-4" />
          </span>
        ) : staff ? (
          <input
            type="checkbox"
            className="size-4 accent-primary"
            aria-label={`Select line ${l.lineNo} of ${l.documentNumber ?? `journal no. ${l.entryNo}`}`}
            checked={checked}
            onChange={onToggle}
          />
        ) : null}
      </TableCell>
      <TableCell>{formatDate(l.date)}</TableCell>
      <TableCell>{sourceLabel(l.sourceType)}</TableCell>
      <TableCell>
        <DrillLink drill={drill}>{l.documentNumber ?? l.entryNo}</DrillLink>
      </TableCell>
      <TableCell>{l.contactName ?? ''}</TableCell>
      <TableCell className="max-w-[280px] truncate" title={l.description ?? l.memo ?? ''}>
        {l.description ?? l.memo ?? ''}
        {l.blocked ? <span className="block text-xs">{l.blocked}</span> : null}
      </TableCell>
      <TableCell>
        {l.accountCode} {l.accountName}
      </TableCell>
      <TableCell>{l.taxCode ?? ''}</TableCell>
      <TableCell className="tnum text-right">{formatAmount(l.amount)}</TableCell>
    </TableRow>
  );
}
